import React from 'react';

const SERIF = "'Cormorant Garamond', Georgia, serif";
const SANS = "'Outfit', sans-serif";

const TermsOfService: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#0C0A14] text-white">
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8 md:py-10">
        <a href="/" className="inline-flex items-center gap-2 text-white/45 hover:text-white/80 text-sm mb-6" style={{ fontFamily: SANS }}>
          Back to Refract
        </a>

        <div className="rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl overflow-hidden">
      <header className="border-b border-white/10 bg-transparent">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 md:px-8 py-6">
          <div>
            <p className="text-sm uppercase tracking-[0.3em] text-white/35" style={{ fontFamily: SANS }}>Refract</p>
            <h1 className="text-4xl md:text-5xl font-light" style={{ fontFamily: SERIF }}>Terms of Service</h1>
          </div>
          <a
            href="/"
            className="rounded-xl border border-white/15 px-4 py-2 text-sm text-white/65 transition hover:border-white/30 hover:text-white"
            style={{ fontFamily: SANS }}
          >
            Back to Refract
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 md:px-8 py-8 leading-relaxed text-white/75" style={{ fontFamily: SANS }}>
        <section className="space-y-2">
          <p className="text-sm text-gray-400">Effective date: February 2, 2026</p>
          <p className="text-sm text-gray-400">Last updated: February 14, 2026</p>
          <p>
            These Terms of Service ("Terms") govern your access to and use of <span className="font-semibold text-white">Refract</span>
             (the "Service"). By accessing or using the Service, you agree to these Terms.
          </p>
          <p>If you do not agree, do not use the Service.</p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">1) The Service</h2>
          <p>
            Refract helps you explore your own photos and videos stored in your Google Drive by providing features like shuffling,
            viewing, searching, and optional AI-assisted discovery.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">2) Eligibility</h2>
          <p>
            You must be at least 13 years old (or the minimum legal age in your country) to use the Service. If you are using the
            Service on behalf of an organization, you represent you have authority to bind that organization.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">3) Accounts and Google connection</h2>
          <p>
            To use certain features, you may need to sign in with Google and authorize access to specific Google data (OAuth scopes).
          </p>
          <ul className="list-inside list-disc space-y-2">
            <li>Provide accurate information where required.</li>
            <li>Keep your account secure.</li>
            <li>Notify us of unauthorized use.</li>
          </ul>
          <p>You can revoke Google access at any time through your Google Account settings.</p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">4) Your content</h2>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">A. Ownership</h3>
            <p>
              You retain all rights to the photos, videos, and other content you access through the Service ("User Content"). Refract
              does not claim ownership over your Google Drive content.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">B. Permission to process User Content</h3>
            <p>
              To provide the Service, you grant Refract a limited, non-exclusive, royalty-free license to access, process, store (as metadata only, e.g., file IDs, names, MIME types, timestamps), and display your User Content only as needed to operate the Service (e.g., retrieve a photo, generate search results, create derived metadata like tags or embeddings if enabled). We do not claim any ownership of your content; this license is solely for providing and improving the Service.
            </p>
            <p>
              This license ends when you disconnect Google or stop using the Service, subject to any reasonable retention periods
              described in the Privacy Policy.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">C. Your representations and warranties</h3>
            <p>
              You represent and warrant that: (i) you own or otherwise have the right to use, display, and authorize Refract to process all User Content you make available through the Service; (ii) your use of the Service and your User Content does not violate these Terms, any applicable law, or any third party's rights; and (iii) you will not use the Service to upload, store, share, or link to content that is illegal or that you do not have the right to use. Any use of the Service that violates these representations may result in immediate suspension or termination of your access and may be reported to appropriate authorities.
            </p>
          </div>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">5) Acceptable use policy</h2>
          <p>
            You must use the Service in compliance with these Terms and all applicable laws. The following conduct and content are strictly prohibited.
          </p>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">A. Prohibited content</h3>
            <p>You may not upload, store, share, or link to any content that is illegal or that you do not have the right to use. Prohibited content includes, but is not limited to:</p>
            <ul className="list-inside list-disc space-y-1 text-gray-300">
              <li>Material that infringes copyright, trademark, or other intellectual property rights without authorization.</li>
              <li>Child sexual abuse material (CSAM) or any exploitation of minors.</li>
              <li>Content that constitutes hate speech, incitement to violence, threats, harassment, or defamation.</li>
              <li>Malware, phishing, or other harmful or deceptive code or material.</li>
              <li>Content that facilitates or promotes sex trafficking, fraud, or other criminal conduct.</li>
              <li>Content that violates the privacy or publicity rights of others, or that you are not authorized to share.</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">B. Prohibited conduct</h3>
            <ul className="list-inside list-disc space-y-1 text-gray-300">
              <li>Using the Service for any unlawful purpose or in violation of these Terms.</li>
              <li>Attempting to gain unauthorized access to our systems, other users' accounts, or any third-party systems.</li>
              <li>Interfering with or disrupting the integrity or performance of the Service.</li>
              <li>Reverse engineering, decompiling, or scraping the Service except as expressly permitted by applicable law.</li>
              <li>Uploading or transmitting malware, viruses, or other harmful code.</li>
              <li>Using the Service to violate others' privacy or intellectual property rights.</li>
            </ul>
          </div>
          <p>
            We reserve the right to remove or disable access to any content that violates this policy and to suspend or terminate accounts that engage in prohibited use. Repeated or serious violations may result in permanent termination and referral to law enforcement where appropriate.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">6) Enforcement</h2>
          <p>
            We reserve the right, but have no obligation, to: (a) remove or disable access to any User Content or other content that we reasonably believe violates these Terms or our Acceptable Use Policy; (b) suspend or terminate your account or access to the Service if you engage in prohibited conduct or if we reasonably believe your use creates legal exposure or harm to others; and (c) respond to lawful requests from law enforcement, rights holders, or government authorities, including subpoenas, court orders, DMCA notices, and other legal process. We may take these actions without prior notice where we deem it necessary to protect the Service, our users, or to comply with applicable law.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">7) DMCA and notice-and-takedown</h2>
          <p>
            Refract respects intellectual property rights and expects users to do the same. We respond to valid notices of alleged copyright infringement under the U.S. Digital Millennium Copyright Act ("DMCA") and similar laws.
          </p>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">Designated agent</h3>
            <p>
              If you are a copyright owner or authorized to act on their behalf, you may submit a DMCA takedown notice to our designated agent. Send notices to:
            </p>
            <p className="font-mono text-sm bg-gray-900/60 p-3 rounded border border-gray-800">
              DMCA Agent – Refract<br />
              Email: <a className="text-blue-400 hover:text-blue-300" href="mailto:userefractapp@gmail.com">userefractapp@gmail.com</a><br />
              Subject line: "DMCA Takedown Notice"
            </p>
            <p>
              Your notice must include: (i) identification of the copyrighted work claimed to have been infringed; (ii) identification of the material that is claimed to be infringing and information reasonably sufficient to permit us to locate it (e.g., file IDs or descriptions, URLs if applicable); (iii) your contact information; (iv) a statement that you have a good-faith belief that use of the material is not authorized by the copyright owner; and (v) a statement, under penalty of perjury, that the information in the notice is accurate and that you are authorized to act on behalf of the copyright owner.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-white">Our response</h3>
            <p>
              We will expeditiously remove or disable access to content that we reasonably believe infringes copyright upon receipt of a valid DMCA notice. We may terminate repeat infringers' access in appropriate circumstances. If you believe your content was removed in error, you may submit a counter-notification to the same address with the information required by the DMCA; we may restore content if the counter-notification is valid and no legal action has been filed.
            </p>
          </div>
          <p className="text-sm text-gray-400">
            For other clearly illegal content (e.g., not copyright-related), you may report it to the same contact. We will review reports and remove or disable access to content that we reasonably believe violates applicable law or our Acceptable Use Policy, and we will cooperate with law enforcement as required by law.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">8) Third-party services</h2>
          <p>
            The Service may integrate with third-party services (e.g., Google APIs) and may link to third-party sites. Those services
            are governed by their own terms and policies. We are not responsible for third-party services outside our control.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">9) Updates and changes</h2>
          <p>
            We may modify, suspend, or discontinue the Service (in whole or part) at any time, including adding or removing features.
            We may also update these Terms. Material changes will be reflected by updating the “Last updated” date, and we may provide
            additional notice.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">10) Fees (if applicable)</h2>
          <p>
            If parts of the Service are paid, you agree to pay the applicable fees and taxes. Payments are non-refundable except where
            required by law or expressly stated.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">11) Disclaimers</h2>
          <p className="uppercase">The Service is provided “as is” and “as available.”</p>
          <p>
            To the maximum extent permitted by law, we disclaim all warranties, express or implied, including implied warranties of
            merchantability, fitness for a particular purpose, and non-infringement.
          </p>
          <ul className="list-inside list-disc space-y-2">
            <li>The Service will be uninterrupted, error-free, or secure.</li>
            <li>Any content will be available at all times.</li>
            <li>AI-generated results will be accurate or complete.</li>
          </ul>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">12) Limitation of liability</h2>
          <p className="uppercase">
            To the maximum extent permitted by law, Refract and its operators will not be liable for indirect, incidental, special,
            consequential, or punitive damages, or any loss of data, profits, revenue, or business opportunity, arising out of or
            related to your use of the Service.
          </p>
          <p>In any event, our total liability for all claims relating to the Service will not exceed the greater of:</p>
          <ul className="list-inside list-disc space-y-2">
            <li>The amount you paid us (if any) in the 12 months before the event giving rise to the claim, or</li>
            <li>$50 (or local equivalent).</li>
          </ul>
          <p>Some jurisdictions do not allow certain limitations, so some of the above may not apply to you.</p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">13) Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Refract and its operators from claims, damages, liabilities, losses, and expenses
            (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your
            violation of laws or third-party rights.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">14) Termination</h2>
          <p>
            You may stop using the Service at any time. We may suspend or terminate your access if we reasonably believe you violated
            these Terms, your use creates risk or legal exposure, or we are required to do so by law.
          </p>
          <p>Upon termination, your right to use the Service ends. Sections that by their nature should survive will survive.</p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">15) Governing law, venue, and compliance</h2>
          <p>These Terms are governed by the laws of the United States. Courts located in Miami, Florida will have exclusive jurisdiction for disputes arising under these Terms, except where prohibited by law.</p>
          <p>
            Where you access the Service from outside the United States (e.g., the European Union, the United Kingdom, or other jurisdictions), you agree that we may comply with applicable local law in those regions, including duties to remove or restrict access to illegal or harmful content (e.g., under the EU Digital Services Act, the UK Online Safety Act, or similar frameworks). We may remove content, suspend or terminate accounts, and cooperate with local authorities as required by applicable law. Our enforcement and moderation decisions are made in good faith to protect users and comply with legal obligations.
          </p>
        </section>

        <section className="mt-10 space-y-4">
          <h2 className="text-xl font-semibold text-white">16) Contact</h2>
          <p>Questions about these Terms:</p>
          <ul className="list-inside list-disc space-y-2">
            <li>Email: userefractapp@gmail.com</li>
          </ul>
        </section>
      </main>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
